import { useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import {
    AbfOrderFundingType,
    BsMetaUtil,
    getNetPosition,
    isLeverageLoop,
    LoanFilterType,
    LoansAggregate,
    LoopPositionExpanded,
    MAX_LOANS_PAGE_SIZE,
    UserLoansFilter,
    UserLoopGroup,
    useLoopOraclePriceQuotes,
    useLoopPnlDisplayValues,
    useUserLoopGroups,
    useUserLoopPositionsPaginated,
    useUserLoopsGroupedByCollateralGroupKey,
    userLoopGroupTableKey
} from "@bridgesplit/abf-react";
import {
    createRows,
    MEDIA,
    MobilePortfolioCards,
    MobilePortfolioPagination,
    MobilePortfolioRowConfig,
    TABLE_CTA_COLUMN_KEY,
    TableColumn,
    TableKey,
    TableRow,
    Table,
    useCheckboxTableController
} from "@bridgesplit/ui";
import { ALL_VALUE, AllValueType, EMPTY_STRING, isAll, SortDirection } from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { useChunkedPaginationController, useClampPaginationOnCountChange } from "app/api/shared";
import { useLoopsPagination } from "app/api/loop";
import { LoanAction } from "app/components/loan/side-card/types";
import { RateDisplay } from "app/components/points/app/common";
import { AppDialog, AppDialogOpen, useAppDialog } from "app/utils";

import { useNavbarHeight } from "../navbar";
import { LoanHealthTag, LoopName, TableCardWrapper, getLoanAssetPath } from "../common";
import { LoopPositionApy } from "../loop/positions/common";
import { LoopsStats } from "./stats";
import { SectionCollapse, SectionSkeleton } from "./PortfolioSections";
import {
    CELL_WIDTH_DEFAULTS,
    getDefaultPaginationController,
    GROUP_CTA_CELL,
    GROUP_HEADER_CELL_TOOLTIPS,
    GroupHealthCell,
    GroupNameCell,
    LoanActionsButton,
    buildMergeKeyByAddress,
    getMergeDisabledReason,
    MissingValueCell,
    PairTokenValueCell,
    useSelectedMergeGroupCount
} from "./common";
import { BulkLoanActionsButton } from "./LoansSection";
import { HealthApyChipStats, PortfolioAllChip, PortfolioTokenChip } from "./mobileCards";

const PAGE_SIZE = 5;
// Every collateral group in one page for the chips, capped at the loans endpoint's server-side max.
const ALL_GROUPS_PAGE_SIZE = MAX_LOANS_PAGE_SIZE;

const LOOPS_ROW_CONFIG: MobilePortfolioRowConfig = {
    headerDataIndex: "asset",
    primaryValueDataIndex: "position",
    statDataIndexes: ["pnl", "health", "apy"]
};

function netPositionUsdFromAggregate(g: UserLoopGroup): number {
    const { collateralUsd, principalUsd, interestAccruedUsd } = g.aggregate;
    return collateralUsd - principalUsd - interestAccruedUsd;
}

// `netPositionTokenAmount` is actually the group's total collateral (see ApiUserLoopGroup doc), so
// scale it by netUsd/collateralUsd to express the net position in collateral tokens at the same
// implied price the USD npv uses.
function netPositionTokenFromAggregate(g: UserLoopGroup): number {
    const { collateralUsd } = g.aggregate;
    if (!collateralUsd) return 0;
    return (g.netPositionTokenAmount * netPositionUsdFromAggregate(g)) / collateralUsd;
}

interface LoopsSectionProps {
    isExpanded: boolean;
    isUsdValueLoading: boolean;
}

export default function LoopsSection({ isExpanded, isUsdValueLoading }: LoopsSectionProps) {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const pagination = useChunkedPaginationController<UserLoopGroup, UserLoansFilter>(
        {
            userAddress: undefined,
            principalMints: undefined,
            collateralMints: undefined,
            collateralIdentifiers: undefined,
            filterType: LoanFilterType.Active,
            orderFundingTypes: AbfOrderFundingType.Loop,
            sortDirection: SortDirection.Desc
        },
        PAGE_SIZE
    );
    const {
        groups,
        totalGroups,
        aggregate: desktopAggregate,
        isLoading
    } = useUserLoopGroups(pagination.backendPagination, isMobile);
    useClampPaginationOnCountChange(pagination.clampPageToCount, totalGroups);

    const visibleGroups = useMemo(() => pagination.visibleData(groups) ?? [], [groups, pagination]);
    const visibleGroupKeys = useMemo(() => visibleGroups.map(userLoopGroupTableKey), [visibleGroups]);
    const { loopsByGroupKey, isLoading: detailsLoading } = useUserLoopsGroupedByCollateralGroupKey(visibleGroupKeys, {
        skip: isMobile
    });

    const mobile = useMobileLoopsView(isMobile);
    const aggregate = isMobile ? mobile.aggregate : desktopAggregate;

    const { selected, setSelected } = useCheckboxTableController();
    const selectedLoanAddresses = useMemo(
        () => Array.from(selected).filter((k): k is string => typeof k === "string"),
        [selected]
    );

    const mergeSource = isMobile ? mobile.mergeSource : loopsByGroupKey;
    const mergeKeyByAddress = useMemo(() => buildMergeKeyByAddress(mergeSource, (p) => p.loanExpanded), [mergeSource]);
    const selectedMergeGroupCount = useSelectedMergeGroupCount(selectedLoanAddresses, mergeKeyByAddress);

    const { open } = useAppDialog();
    const { height: navbarHeight } = useNavbarHeight();

    const tableRows = useMemo(() => createRows<UserLoopGroup>(visibleGroups, userLoopGroupTableKey), [visibleGroups]);
    const groupedColumnDefs = useMemo(() => getGroupColumnDefs(loopsByGroupKey), [loopsByGroupKey]);
    const itemColumnDefs = useMemo(() => getLoopDetailColumnDefs(open), [open]);

    const mergeAction = (
        <BulkLoanActionsButton
            selectedLoanAddresses={selectedLoanAddresses}
            mergeDisabledReason={getMergeDisabledReason(selectedMergeGroupCount, COPY.LOOP_TERM_PLURAL.toLowerCase())}
        />
    );

    if (isUsdValueLoading) return <SectionSkeleton />;

    return (
        <SectionCollapse isExpanded={isExpanded}>
            <LoopsStats aggregate={aggregate} />
            <TableCardWrapper flushOnMobile>
                {isMobile ? (
                    <MobilePortfolioCards<UserLoopGroup, LoopPositionExpanded>
                        filterLabel="Filter by collateral"
                        activeFilter={mobile.activeFilter}
                        onFilterChange={mobile.onFilterChange}
                        groups={mobile.chipRows}
                        renderChip={(row) => <LoopChipContent group={row?.data} />}
                        groupsLoading={mobile.allGroupsLoading}
                        positions={mobile.positionRows}
                        positionColumns={itemColumnDefs}
                        rowConfig={LOOPS_ROW_CONFIG}
                        onPositionNavigatePath={(p) => {
                            const loan = p.loanExpanded?.loan;
                            return loan ? getLoanAssetPath(loan) : undefined;
                        }}
                        positionsLoading={mobile.positionsLoading}
                        pagination={mobile.pagination}
                        hasHealth
                        hasSelect
                        selection={{
                            selected,
                            setSelected,
                            getKey: (p) => p.loanExpanded.loan.address,
                            bulkActionNode: mergeAction
                        }}
                        stickyTop={navbarHeight}
                        emptyPlaceholder={`No active ${COPY.LOOP_TERM_PLURAL.toLowerCase()}`}
                        listLabel={mobile.listLabel}
                        allCard={
                            <PortfolioAllChip
                                loading={!aggregate}
                                count={aggregate?.count}
                                valueUsd={
                                    aggregate
                                        ? aggregate.collateralUsd -
                                          aggregate.principalUsd -
                                          aggregate.interestAccruedUsd
                                        : undefined
                                }
                                assetCount={mobile.allGroupsTotal}
                                stats={
                                    <HealthApyChipStats
                                        healths={mobile.allHealths}
                                        apy={aggregate?.wAvgApy}
                                        loading={!aggregate}
                                    />
                                }
                            />
                        }
                    />
                ) : (
                    <Table<UserLoopGroup, LoopPositionExpanded>
                        rows={tableRows}
                        columns={groupedColumnDefs}
                        grouping={{
                            columns: itemColumnDefs,
                            getItems: (groupKey) => loopsByGroupKey?.get(groupKey as string),
                            getDetailKey: (detail) => detail.loanExpanded.loan.address,
                            isDetailsLoading: detailsLoading,
                            onDetailRowNavigatePath: (detail) => {
                                const loan = detail.loanExpanded?.loan;
                                return loan ? getLoanAssetPath(loan) : undefined;
                            }
                        }}
                        selection={{ selected, setSelected, expandOnGroupSelect: true }}
                        toolbarActions={mergeAction}
                        paginationProps={{
                            type: "api",
                            pageSize: pagination.pageSize,
                            totalDataCount: totalGroups,
                            controller: getDefaultPaginationController(pagination)
                        }}
                        loading={isLoading}
                        emptyPlaceholder={`No active ${COPY.LOOP_TERM_PLURAL.toLowerCase()}`}
                        showViewToggle
                        viewToggleStorageKey="PORTFOLIO_LOOPS_VIEW"
                    />
                )}
            </TableCardWrapper>
        </SectionCollapse>
    );
}

function useMobileLoopsView(isMobile: boolean) {
    const [activeFilter, setActiveFilter] = useState<AllValueType | TableKey>(ALL_VALUE);

    const {
        groups: allGroups,
        totalGroups: allGroupsTotal,
        aggregate,
        isLoading: allGroupsLoading
    } = useUserLoopGroups(
        {
            filterType: LoanFilterType.Active,
            orderFundingTypes: AbfOrderFundingType.Loop,
            sortDirection: SortDirection.Desc,
            page: 0,
            pageSize: ALL_GROUPS_PAGE_SIZE
        },
        !isMobile
    );

    const positionsPagination = useLoopsPagination();
    const {
        positions: positionsChunk,
        totalCount: positionsTotal,
        isLoading: positionsLoading
    } = useUserLoopPositionsPaginated({
        pagination: positionsPagination.backendPagination,
        collateralGroupKey: activeFilter,
        skip: !isMobile
    });
    useClampPaginationOnCountChange(positionsPagination.clampPageToCount, positionsTotal);

    const visiblePositions = useMemo(
        () => positionsPagination.visibleData(positionsChunk),
        [positionsChunk, positionsPagination]
    );

    const chipRows = useMemo(() => createRows<UserLoopGroup>(allGroups ?? [], userLoopGroupTableKey), [allGroups]);
    // Stay undefined while positions load so the list skeletons instead of flashing the empty state.
    const positionRows = useMemo(
        () =>
            visiblePositions
                ? createRows<LoopPositionExpanded>(visiblePositions, (p) => p.loanExpanded.loan.address)
                : undefined,
        [visiblePositions]
    );

    const onFilterChange = (next: AllValueType | TableKey) => {
        setActiveFilter(next);
        positionsPagination.resetPagination();
    };

    const activeGroupSymbol = useMemo(() => {
        if (isAll(activeFilter)) return undefined;
        const group = allGroups?.find((g) => userLoopGroupTableKey(g) === activeFilter);
        return group ? BsMetaUtil.getSymbol(group.collateralMetadata) : undefined;
    }, [activeFilter, allGroups]);
    const listLabel = `${activeGroupSymbol ?? "All"} ${COPY.LOOP_TERM_PLURAL.toLowerCase()}`;
    const allHealths = useMemo(() => (allGroups ?? []).flatMap((g) => g.rowHealths), [allGroups]);

    const mergeSource = useMemo(
        () => (visiblePositions ? new Map([[EMPTY_STRING, visiblePositions]]) : undefined),
        [visiblePositions]
    );

    const pagination: MobilePortfolioPagination = {
        page: positionsPagination.page,
        setPage: positionsPagination.setPage,
        pageSize: positionsPagination.pageSize,
        totalCount: positionsTotal
    };

    return {
        activeFilter,
        onFilterChange,
        chipRows,
        allGroupsLoading,
        allGroupsTotal,
        aggregate,
        positionRows,
        positionsLoading,
        pagination,
        listLabel,
        allHealths,
        mergeSource
    };
}

function LoopChipContent({ group }: { group: UserLoopGroup | undefined }) {
    return (
        <PortfolioTokenChip
            metadata={group?.collateralMetadata}
            count={group?.aggregate.count}
            valueUsd={group ? netPositionUsdFromAggregate(group) : undefined}
            tokenAmount={group ? netPositionTokenFromAggregate(group) : undefined}
            stats={<HealthApyChipStats healths={group?.rowHealths ?? []} apy={group?.wAvgNetApy} loading={!group} />}
        />
    );
}

function getGroupColumnDefs(loopsByGroupKey: Map<string, LoopPositionExpanded[]> | undefined) {
    const groupColumnDefs: TableColumn<UserLoopGroup>[] = [
        {
            dataIndex: "asset",
            title: "Asset",
            render: (row: TableRow<UserLoopGroup>) => (
                <GroupNameCell metadata={row.data.collateralMetadata} count={row.data.aggregate.count} />
            )
        },
        {
            dataIndex: "position",
            title: COPY.USER_POSITION_TERM,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row: TableRow<UserLoopGroup>) => (
                <PairTokenValueCell
                    usdAmount={netPositionUsdFromAggregate(row.data)}
                    tokenAmount={netPositionTokenFromAggregate(row.data)}
                    metadata={row.data.collateralMetadata}
                    isRawTokenAmount
                />
            )
        },
        {
            dataIndex: "pnl",
            title: "P&L",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row: TableRow<UserLoopGroup>) => (
                <GroupPnlCell
                    aggregate={row.data.aggregate}
                    loops={loopsByGroupKey?.get(userLoopGroupTableKey(row.data))}
                />
            )
        },

        {
            dataIndex: "health",
            title: "Health",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.health,
            render: (row: TableRow<UserLoopGroup>) => <GroupHealthCell healths={row.data.rowHealths} />
        },
        {
            dataIndex: "apy",
            title: COPY.APY_TERM,
            tooltip: GROUP_HEADER_CELL_TOOLTIPS.vaults.apy,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.apy,
            render: (row: TableRow<UserLoopGroup>) => (
                <RateDisplay apy={row.data.wAvgNetApy} variant="body1" color="body" isFeatured underline={false} />
            )
        },

        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: GROUP_CTA_CELL
        }
    ];

    return groupColumnDefs;
}

export function getLoopDetailColumnDefs(open: AppDialogOpen): TableColumn<LoopPositionExpanded>[] {
    return [
        {
            dataIndex: "asset",
            title: "Asset",
            render: (row) => (
                <LoopName loopExpanded={row.data.loopExpanded} variant="body1" condensed fullSymbolOnHover />
            )
        },
        {
            dataIndex: "position",
            title: COPY.USER_POSITION_TERM,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => <PositionCell loopPosition={row.data} />
        },
        {
            dataIndex: "pnl",
            title: "P&L",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) => <DetailPnlCell loopPosition={row.data} />
        },
        {
            dataIndex: "health",
            title: "Health",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.health,
            render: (row) => <LoanHealthTag loan={row.data.loanExpanded} variant="body1" textOnly />
        },
        {
            dataIndex: "apy",
            title: COPY.APY_TERM,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.apy,
            render: (row) =>
                row.data.loanExpanded?.netApyLive == null ? (
                    <MissingValueCell />
                ) : (
                    <LoopPositionApy loopPosition={row.data} hideApyText={true} />
                )
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: (row) => <CtaCell loopPosition={row.data} open={open} />
        }
    ];
}

/* Loop cells */
function PositionCell({ loopPosition }: { loopPosition: LoopPositionExpanded }) {
    const { principalOracle, collateralOracle } = useLoopOraclePriceQuotes(loopPosition.loopExpanded);
    const net = getNetPosition(loopPosition, principalOracle?.usdPrice, collateralOracle?.usdPrice);
    return (
        <PairTokenValueCell
            usdAmount={net?.usdNpv}
            tokenAmount={net?.tokenNpv}
            metadata={loopPosition.loopExpanded?.collateralToken}
        />
    );
}

// Per-position P&L: leverage loops use cost-basis `usdPnl`, non-leverage the in-kind PnL (backend-stamped).
function DetailPnlCell({ loopPosition }: { loopPosition: LoopPositionExpanded }) {
    const { displayUsdPnl } = useLoopPnlDisplayValues(loopPosition);
    return <PairTokenValueCell usdAmount={displayUsdPnl ?? 0} primarySignDisplay />;
}

// Group P&L: pick the field by loop type (leverage = all loops leverage-funded); the other is a fallback.
function GroupPnlCell({ aggregate, loops }: { aggregate: LoansAggregate; loops: LoopPositionExpanded[] | undefined }) {
    const isLeverage = loops?.length ? loops.every((lp) => isLeverageLoop(lp.loopExpanded)) : undefined;
    const usdPnl =
        isLeverage === false
            ? (aggregate.tokenUsdPnl ?? aggregate.pnlUsd)
            : (aggregate.pnlUsd ?? aggregate.tokenUsdPnl);
    return <PairTokenValueCell usdAmount={usdPnl} />;
}

function CtaCell({ loopPosition, open }: { loopPosition: LoopPositionExpanded; open: AppDialogOpen }) {
    const onActionClick = (action: LoanAction) => {
        const loanExpanded = loopPosition.loanExpanded;
        if (!loanExpanded) return;
        open(AppDialog.LoanActions, {
            loan: loanExpanded,
            isDrawer: action.isDrawer,
            action: {
                component: action.component,
                dialogHeaderLabel: action.dialogHeaderLabel
            }
        });
    };
    return (
        <LoanActionsButton
            onActionClick={onActionClick}
            loanExpanded={loopPosition.loanExpanded}
            loopPosition={loopPosition}
            isNestedDialogView={false}
        />
    );
}
